/**
 * SettingsMenu - In-game settings panel for audio, graphics, controls, language.
 * Covers tasks 10.6 (settings menu), 10.7 (accessibility/color-blind mode),
 * 10.8 (language), 4.11 (volume controls), 8.7 (frame rate limit),
 * 6.7 (weather toggle), 5.8 (custom match settings), 7.3 (key bindings).
 */

import * as GUI from "babylonjs-gui";
import { AudioSystem } from "../systems/AudioSystem";
import { SaveSystem, Preferences } from "../systems/SaveSystem";
import { LocalizationSystem, SUPPORTED_LANGUAGES } from "../systems/LocalizationSystem";
import { WeatherSystem, WeatherType } from "../systems/WeatherSystem";

export class SettingsMenu {
  private _texture: GUI.AdvancedDynamicTexture;
  private _container: GUI.Rectangle;
  private _onClose: () => void;
  private _weather?: WeatherSystem;
  private _onPrefsChange?: (prefs: Preferences) => void;

  constructor(
    onClose: () => void,
    weather?: WeatherSystem,
    onPrefsChange?: (prefs: Preferences) => void
  ) {
    this._onClose = onClose;
    this._weather = weather;
    this._onPrefsChange = onPrefsChange;

    this._texture = GUI.AdvancedDynamicTexture.CreateFullscreenUI("settingsMenu");
    this._container = new GUI.Rectangle("settingsBg");
    this._container.width = "100%";
    this._container.height = "100%";
    this._container.thickness = 0;
    this._container.background = "rgba(0,0,0,0.88)";
    this._container.isVisible = false;
    this._texture.addControl(this._container);

    this._buildUI();
  }

  private _buildUI(): void {
    const prefs = SaveSystem.getPreferences();

    // Title
    const title = new GUI.TextBlock("settingsTitle", "⚙ SETTINGS");
    title.color = "#00ff88";
    title.fontSize = 36;
    title.fontWeight = "900";
    title.fontFamily = "Orbitron, sans-serif";
    title.top = "-300px";
    this._container.addControl(title);

    // Scrollable panel
    const panel = new GUI.ScrollViewer("settingsScroll");
    panel.width = "600px";
    panel.height = "520px";
    panel.top = "30px";
    panel.thickness = 0;
    panel.background = "transparent";
    this._container.addControl(panel);

    const stack = new GUI.StackPanel("settingsStack");
    stack.width = "560px";
    stack.isVertical = true;
    panel.addControl(stack);

    // ── AUDIO ────────────────────────────────────────────────────────
    this._addSectionHeader(stack, "🔊 AUDIO");

    this._addSlider(stack, "MASTER VOLUME", prefs.masterVolume, 0, 1, (v) => {
      AudioSystem.setMasterVolume(v);
      SaveSystem.setPreference("masterVolume", v);
    });
    this._addSlider(stack, "SFX VOLUME", prefs.sfxVolume, 0, 1, (v) => {
      AudioSystem.setSFXVolume(v);
      SaveSystem.setPreference("sfxVolume", v);
    });
    this._addSlider(stack, "MUSIC VOLUME", prefs.musicVolume, 0, 1, (v) => {
      AudioSystem.setMusicVolume(v);
      SaveSystem.setPreference("musicVolume", v);
    });
    this._addSlider(stack, "COMMENTARY", prefs.commentaryVolume, 0, 1, (v) => {
      AudioSystem.setCommentaryVolume(v);
      SaveSystem.setPreference("commentaryVolume", v);
    });

    // ── MATCH SETTINGS ───────────────────────────────────────────────
    this._addSectionHeader(stack, "⚽ MATCH SETTINGS");

    // Match duration
    const durations = [1, 2, 3, 5, 10];
    this._addDropdown(stack, "MATCH DURATION", durations.map(d => `${d} min`), prefs.matchDuration - 1, (idx) => {
      SaveSystem.setPreference("matchDuration", durations[idx]);
      if (this._onPrefsChange) this._onPrefsChange(SaveSystem.getPreferences());
    });

    // Weather
    const weatherOptions: WeatherType[] = ["none", "rain", "snow"];
    this._addDropdown(stack, "WEATHER", ["NONE", "RAIN", "SNOW"], weatherOptions.indexOf(prefs.weather), (idx) => {
      const w = weatherOptions[idx];
      SaveSystem.setPreference("weather", w);
      if (this._weather) this._weather.setWeather(w);
      if (this._onPrefsChange) this._onPrefsChange(SaveSystem.getPreferences());
    });

    // Frame rate limit
    const fpsOptions = [0, 30, 60, 120];
    this._addDropdown(stack, "FRAME RATE LIMIT", ["UNLIMITED", "30 FPS", "60 FPS", "120 FPS"], fpsOptions.indexOf(prefs.frameRateLimit) >= 0 ? fpsOptions.indexOf(prefs.frameRateLimit) : 0, (idx) => {
      SaveSystem.setPreference("frameRateLimit", fpsOptions[idx]);
      if (this._onPrefsChange) this._onPrefsChange(SaveSystem.getPreferences());
    });

    // ── DISPLAY ───────────────────────────────────────────────────────
    this._addSectionHeader(stack, "🎨 DISPLAY");

    this._addToggle(stack, "SHOW RADAR", prefs.showRadar, (v) => {
      SaveSystem.setPreference("showRadar", v);
      if (this._onPrefsChange) this._onPrefsChange(SaveSystem.getPreferences());
    });
    this._addToggle(stack, "SHOW POSSESSION", prefs.showPossession, (v) => {
      SaveSystem.setPreference("showPossession", v);
      if (this._onPrefsChange) this._onPrefsChange(SaveSystem.getPreferences());
    });
    this._addToggle(stack, "SHOW SHOTS ON TARGET", prefs.showShotsOnTarget, (v) => {
      SaveSystem.setPreference("showShotsOnTarget", v);
      if (this._onPrefsChange) this._onPrefsChange(SaveSystem.getPreferences());
    });
    this._addToggle(stack, "CONTROL HINTS", prefs.showControls, (v) => {
      SaveSystem.setPreference("showControls", v);
      if (this._onPrefsChange) this._onPrefsChange(SaveSystem.getPreferences());
    });
    this._addToggle(stack, "COLOR BLIND MODE", prefs.colorBlindMode, (v) => {
      SaveSystem.setPreference("colorBlindMode", v);
      if (this._onPrefsChange) this._onPrefsChange(SaveSystem.getPreferences());
    });
    this._addToggle(stack, "HIGH PERF CROWD", prefs.highPerformanceCrowd, (v) => {
      SaveSystem.setPreference("highPerformanceCrowd", v);
      if (this._onPrefsChange) this._onPrefsChange(SaveSystem.getPreferences());
    });

    // ── LANGUAGE ─────────────────────────────────────────────────────
    this._addSectionHeader(stack, "🌍 LANGUAGE");

    const langNames = SUPPORTED_LANGUAGES.map(l => l.name);
    const currentLangIdx = SUPPORTED_LANGUAGES.findIndex(l => l.code === prefs.language);
    this._addDropdown(stack, "LANGUAGE", langNames, Math.max(0, currentLangIdx), (idx) => {
      const lang = SUPPORTED_LANGUAGES[idx];
      LocalizationSystem.setLanguage(lang.code);
      SaveSystem.setPreference("language", lang.code);
      if (this._onPrefsChange) this._onPrefsChange(SaveSystem.getPreferences());
    });

    // ── DATA ──────────────────────────────────────────────────────────
    this._addSectionHeader(stack, "💾 DATA");

    // Export/Import buttons
    const dataRow = new GUI.StackPanel("dataRow");
    dataRow.isVertical = false;
    dataRow.height = "46px";
    dataRow.paddingTop = "6px";
    stack.addControl(dataRow);

    const exportBtn = this._createButton("Export Save", "#888888", "rgba(40,40,40,0.8)");
    exportBtn.onPointerUpObservable.add(() => SaveSystem.exportData());
    dataRow.addControl(exportBtn);

    const spacer = new GUI.Rectangle("ds"); spacer.width = "10px"; spacer.thickness = 0; dataRow.addControl(spacer);

    const resetBtn = this._createButton("Reset Save", "#ff3366", "rgba(80,10,10,0.8)");
    resetBtn.onPointerUpObservable.add(() => {
      if (confirm("Reset all save data?")) SaveSystem.reset();
    });
    dataRow.addControl(resetBtn);

    // ── CLOSE ─────────────────────────────────────────────────────────
    const closeBtn = GUI.Button.CreateSimpleButton("closeBtn", "✓ CLOSE");
    closeBtn.width = "200px";
    closeBtn.height = "50px";
    closeBtn.color = "#00ff88";
    closeBtn.cornerRadius = 4;
    closeBtn.thickness = 1;
    closeBtn.background = "rgba(0, 80, 40, 0.8)";
    closeBtn.fontSize = 16;
    closeBtn.fontWeight = "700";
    closeBtn.fontFamily = "Orbitron, sans-serif";
    closeBtn.top = "310px";
    closeBtn.onPointerUpObservable.add(() => {
      SaveSystem.save();
      this.setVisible(false);
      this._onClose();
    });
    this._container.addControl(closeBtn);
  }

  private _addSectionHeader(stack: GUI.StackPanel, text: string): void {
    const header = new GUI.TextBlock(`sh_${text}`, text);
    header.color = "#00ff88";
    header.fontSize = 13;
    header.fontWeight = "700";
    header.fontFamily = "Orbitron, sans-serif";
    header.height = "34px";
    header.textHorizontalAlignment = GUI.Control.HORIZONTAL_ALIGNMENT_LEFT;
    header.paddingTop = "10px";
    stack.addControl(header);
  }

  private _addSlider(
    stack: GUI.StackPanel,
    label: string,
    initial: number,
    min: number,
    max: number,
    onChange: (v: number) => void
  ): void {
    const row = new GUI.StackPanel(`row_${label}`);
    row.isVertical = false;
    row.height = "36px";
    row.paddingTop = "4px";
    stack.addControl(row);

    const lbl = new GUI.TextBlock(`lbl_${label}`, label);
    lbl.color = "white";
    lbl.fontSize = 11;
    lbl.fontFamily = "Orbitron, sans-serif";
    lbl.width = "180px";
    lbl.textHorizontalAlignment = GUI.Control.HORIZONTAL_ALIGNMENT_LEFT;
    row.addControl(lbl);

    const slider = new GUI.Slider();
    slider.minimum = min;
    slider.maximum = max;
    slider.value = initial;
    slider.width = "250px";
    slider.height = "18px";
    slider.color = "#00ff88";
    slider.background = "rgba(255,255,255,0.15)";
    slider.thumbWidth = 18;
    slider.thumbColor = "#00ff88";
    slider.isThumbCircle = true;
    slider.onValueChangedObservable.add(onChange);
    row.addControl(slider);

    const valText = new GUI.TextBlock(`val_${label}`, `${Math.round(initial * 100)}%`);
    valText.color = "#aaaaaa";
    valText.fontSize = 10;
    valText.fontFamily = "Orbitron, sans-serif";
    valText.width = "50px";
    slider.onValueChangedObservable.add(v => {
      valText.text = `${Math.round(v * 100)}%`;
    });
    row.addControl(valText);
  }

  private _addToggle(
    stack: GUI.StackPanel,
    label: string,
    initial: boolean,
    onChange: (v: boolean) => void
  ): void {
    const row = new GUI.StackPanel(`row_${label}`);
    row.isVertical = false;
    row.height = "32px";
    row.paddingTop = "4px";
    stack.addControl(row);

    const lbl = new GUI.TextBlock(`lbl_${label}`, label);
    lbl.color = "white";
    lbl.fontSize = 11;
    lbl.fontFamily = "Orbitron, sans-serif";
    lbl.width = "280px";
    lbl.textHorizontalAlignment = GUI.Control.HORIZONTAL_ALIGNMENT_LEFT;
    row.addControl(lbl);

    const cb = new GUI.Checkbox();
    cb.width = "20px";
    cb.height = "20px";
    cb.isChecked = initial;
    cb.color = "#00ff88";
    cb.onIsCheckedChangedObservable.add(onChange);
    row.addControl(cb);
  }

  private _addDropdown(
    stack: GUI.StackPanel,
    label: string,
    options: string[],
    initial: number,
    onChange: (idx: number) => void
  ): void {
    const row = new GUI.StackPanel(`row_${label}`);
    row.isVertical = false;
    row.height = "36px";
    row.paddingTop = "4px";
    stack.addControl(row);

    const lbl = new GUI.TextBlock(`lbl_${label}`, label);
    lbl.color = "white";
    lbl.fontSize = 11;
    lbl.fontFamily = "Orbitron, sans-serif";
    lbl.width = "180px";
    lbl.textHorizontalAlignment = GUI.Control.HORIZONTAL_ALIGNMENT_LEFT;
    row.addControl(lbl);

    let currentIdx = Math.max(0, Math.min(initial, options.length - 1));

    const valBtn = new GUI.Rectangle(`valBtn_${label}`);
    valBtn.width = "200px";
    valBtn.height = "28px";
    valBtn.cornerRadius = 3;
    valBtn.thickness = 1;
    valBtn.color = "#555555";
    valBtn.background = "rgba(40,40,40,0.8)";
    valBtn.isHitTestVisible = true;
    valBtn.isPointerBlocker = true;
    row.addControl(valBtn);

    const valText = new GUI.TextBlock(`valTxt_${label}`, options[currentIdx] || "");
    valText.color = "white";
    valText.fontSize = 11;
    valText.fontFamily = "Orbitron, sans-serif";
    valBtn.addControl(valText);

    // Arrow buttons
    const prevBtn = this._createSmallButton("<");
    prevBtn.onPointerUpObservable.add(() => {
      currentIdx = (currentIdx - 1 + options.length) % options.length;
      valText.text = options[currentIdx];
      onChange(currentIdx);
    });
    row.addControl(prevBtn);

    const spacer = new GUI.Rectangle(`sp_${label}`);
    spacer.width = "4px";
    spacer.thickness = 0;
    row.addControl(spacer);

    const nextBtn = this._createSmallButton(">");
    nextBtn.onPointerUpObservable.add(() => {
      currentIdx = (currentIdx + 1) % options.length;
      valText.text = options[currentIdx];
      onChange(currentIdx);
    });
    row.addControl(nextBtn);
  }

  private _createSmallButton(label: string): GUI.Rectangle {
    const btn = new GUI.Rectangle(`smallBtn_${label}_${Date.now()}`);
    btn.width = "28px";
    btn.height = "28px";
    btn.cornerRadius = 3;
    btn.thickness = 1;
    btn.color = "#555555";
    btn.background = "rgba(40,40,40,0.8)";
    btn.isHitTestVisible = true;
    btn.isPointerBlocker = true;

    const t = new GUI.TextBlock();
    t.text = label;
    t.color = "white";
    t.fontSize = 14;
    btn.addControl(t);

    btn.onPointerEnterObservable.add(() => btn.background = "rgba(80,80,80,0.9)");
    btn.onPointerOutObservable.add(() => btn.background = "rgba(40,40,40,0.8)");
    return btn;
  }

  private _createButton(label: string, color: string, bg: string): GUI.Rectangle {
    const btn = new GUI.Rectangle(`btn_${label}`);
    btn.width = "130px";
    btn.height = "36px";
    btn.cornerRadius = 3;
    btn.thickness = 1;
    btn.color = color;
    btn.background = bg;
    btn.isHitTestVisible = true;
    btn.isPointerBlocker = true;

    const t = new GUI.TextBlock();
    t.text = label;
    t.color = "white";
    t.fontSize = 11;
    t.fontFamily = "Orbitron, sans-serif";
    btn.addControl(t);

    btn.onPointerEnterObservable.add(() => btn.alpha = 0.8);
    btn.onPointerOutObservable.add(() => btn.alpha = 1);
    return btn;
  }

  setVisible(visible: boolean): void {
    this._container.isVisible = visible;
  }

  get isVisible(): boolean {
    return this._container.isVisible;
  }

  dispose(): void {
    this._texture.dispose();
  }
}
